import {decorate, observable} from 'mobx';
import {faTag} from '@fortawesome/free-solid-svg-icons';
import {BaseModel} from './base';
import i18n from '../i18n/i18n';
import {transformation} from '../utils/transformations';
import {displayablePropertyParam} from './displayablePropertyParam';
import {CustomPropertyTypes} from './customProperty';
import {displayableProperty} from './displayableProperty';
import {InspectionLot} from './inspectionLot';
import {InspectionTaskGroup} from './inspectionTaskGroup';
import {Workplace} from './workplace';

/**
 * translations:
 * t('inspectionTask.model.attributes.priorityType.NORMAL')
 * t('inspectionTask.model.attributes.priorityType.CRITICAL')
 */
export const InspectionTaskPriority = Object.freeze({
  NORMAL: 'NORMAL',
  CRITICAL: 'CRITICAL',
});

/**
 * translations:
 * t('inspectionTask.model.attributes.graphType.none')
 * t('inspectionTask.model.attributes.graphType.LineChart')
 * t('inspectionTask.model.attributes.graphType.QualityIndicatorChart')
 */
export const InspectionTaskGraph = Object.freeze({
  none: 'none',
  LineChart: 'LineChart',
  QualityIndicatorChart: 'QualityIndicatorChart',
});

/**
 * translations:
 * t('inspectionTask.model.attributes.inspectionTypes.Qualitative')
 * t('inspectionTask.model.attributes.inspectionTypes.Quantitative')
 * t('inspectionTask.model.attributes.inspectionTypes.FactoryCode')
 * t('inspectionTask.model.attributes.inspectionTypes.BestBeforeDate')
 */
export const InspectionTaskType = Object.freeze({
  Qualitative: 'Qualitative',
  Quantitative: 'Quantitative',
  FactoryCode: 'FactoryCode',
  BestBeforeDate: 'BestBeforeDate',
});

export class InspectionTask extends BaseModel {
  static faIcon = faTag;

  searchableProperties = ['id', 'name', 'description'];

  displayableProperties = [
    displayableProperty({
      key: 'inspectionType',
      title: i18n.t('inspectionTask.model.attributes.inspectionType.label'),
      params: [displayablePropertyParam({path: 'inspectionType', transform: transformation.none})],
      template: '{value}',
    }),
    displayableProperty({
      key: 'name',
      title: i18n.t('inspectionTask.model.attributes.name.label'),
      params: [displayablePropertyParam({path: 'name', transform: transformation.none})],
      template: '{value}',
    }),
    displayableProperty({
      key: 'description',
      title: i18n.t('inspectionTask.model.attributes.description.label'),
      params: [displayablePropertyParam({path: 'description', transform: transformation.none})],
      template: '{value}',
    }),
    displayableProperty({
      key: 'characteristicNo',
      title: i18n.t('inspectionTask.model.attributes.characteristicNo.label'),
      params: [displayablePropertyParam({path: 'characteristicNo', transform: transformation.none})],
      template: '{value}',
    }),
    displayableProperty({
      key: 'operationNo',
      title: i18n.t('inspectionTask.model.attributes.operationNo.label'),
      params: [displayablePropertyParam({path: 'operationNo', transform: transformation.none})],
      template: '{value}',
    }),
    displayableProperty({
      key: 'priority',
      title: i18n.t('inspectionTask.model.attributes.priority.label'),
      params: [displayablePropertyParam({path: 'priority', transform: transformation.none})],
      template: '{value}',
    }),
  ];

  nestedModels = ['qualitative', 'quantitative'];

  populateAttributesFromStore(rootStore) {
    super.populateAttributesFromStore(rootStore);
    this.documents = rootStore.documentStore.getByIds(this.documentIds);
    this.workplace = rootStore.workplaceStore.getById(this.workplaceId);
    this.inspectionLot = rootStore.inspectionLotStore.getById(this.inspectionLotId);
    this.deactivatedByUser = rootStore.userStore.getById(this.deactivatedBy);
    this.inspectionTaskGroup = rootStore.inspectionTaskGroupStore.getById(this.inspectionTaskGroupId);
  }

  static allDisplayableProperties(rootStore, pathPrefix = '', titlePrefix = '') {
    const allDisplayableProperties = super.allDisplayableProperties(rootStore, pathPrefix, titlePrefix);
    allDisplayableProperties.push(
      ...this.displayableCustomProperties(rootStore, CustomPropertyTypes.InspectionTask, pathPrefix, titlePrefix)
    );
    const inspectionLotPrefix = `${titlePrefix}${i18n.t('inspectionLot.model.one')} > `;
    allDisplayableProperties.push(
      ...InspectionLot.allDisplayableProperties(rootStore, `${pathPrefix}inspectionLot.`, inspectionLotPrefix)
    );
    const inspectionTaskGroupPrefix = `${titlePrefix}${i18n.t('inspectionTaskGroup.model.one')} > `;
    allDisplayableProperties.push(
      ...InspectionTaskGroup.allDisplayableProperties(
        rootStore,
        `${pathPrefix}inspectionTaskGroup.`,
        inspectionTaskGroupPrefix
      )
    );
    const workplacePrefix = `${titlePrefix}${i18n.t('workplace.model.one')} > `;
    allDisplayableProperties.push(
      ...Workplace.allDisplayableProperties(rootStore, `${pathPrefix}workplace.`, workplacePrefix)
    );

    return allDisplayableProperties;
  }

  static prepareApiPayload(model) {
    if (model.inspectionType === InspectionTaskType.Qualitative
      || model.inspectionType === InspectionTaskType.FactoryCode
      || model.inspectionType === InspectionTaskType.BestBeforeDate) {
      model.qualitative.catalogFeatures.forEach((el) => delete el.id);
    }
    if (model.inspectionType === InspectionTaskType.Quantitative) {
      model.quantitative.toleranceLow = model.quantitative.toleranceLow !== '' ? model.quantitative.toleranceLow : null;
      model.quantitative.toleranceHigh = model.quantitative.toleranceHigh !== ''
        ? model.quantitative.toleranceHigh
        : null;
      model.quantitative.warningLow = model.quantitative.warningLow !== '' ? model.quantitative.warningLow : null;
      model.quantitative.warningHigh = model.quantitative.warningHigh !== '' ? model.quantitative.warningHigh : null;
      model.quantitative.plausibleLow = model.quantitative.plausibleLow !== '' ? model.quantitative.plausibleLow : null;
      model.quantitative.plausibleHigh = model.quantitative.plausibleHigh !== ''
        ? model.quantitative.plausibleHigh
        : null;
      model.quantitative.desiredValue = model.quantitative.desiredValue !== '' ? model.quantitative.desiredValue : null;
    }
    return {
      ...model,
      checkAtStartOffset: model.checkAtStartOffset || null,
      description: model.description || null,
      details: model.details || null,
      phaseId: model.phaseId || null,
      inspectionTaskGroupId: model.inspectionTaskGroupId || null,
      requiredRoleId: model.requiredRoleId || null,
      interval: model.interval || null,
      intervalNotAccepted: model.intervalNotAccepted || null,
      intervalEnd: model.intervalEnd || null,
      dueDateFromInspectionLot: model.dueDateFromInspectionLot || null,
      locationCode: model.locationCode || null,
      operationNo: model.operationNo || null,
      documentIds: model.documentIds?.length ? model.documentIds : [],
      templateHierarchyIds: model.templateHierarchyIds.length ? model.templateHierarchyIds : [],
      isTemplateTask: true,
    };
  }

  get documentsTree() {
    return this.documents.map((document) => ({
      title: document.label,
      key: document.id,
      isLeaf: true,
      icon: document.icon,
      document,
    }));
  }
}

decorate(InspectionTask, {
  id: observable,
  inspectionLotId: observable,
  inspectionLot: observable,
  inspectionType: observable,
  name: observable,
  description: observable,
  details: observable,
  workplace: observable,
  workplaceId: observable,
  phaseId: observable,
  characteristicNo: observable,
  inspectionTaskGroupId: observable,
  inspectionTaskGroup: observable,
  requiredRoleId: observable,
  interval: observable,
  intervalNotAccepted: observable,
  checkAtStart: observable,
  checkAtStartOffset: observable,
  checkAtEnd: observable,
  intervalEnd: observable,
  dueDateFromInspectionLot: observable,
  isObligatory: observable,
  mustBeAccepted: observable,
  isOverruleAllowed: observable,
  showUntilAccepted: observable,
  onceForAllVirtualWorkplaces: observable,
  showOnSiblingVirtualWorkplaces: observable,
  locationCode: observable,
  isAutoValuate: observable,
  graph: observable,
  hideQualityIndicator: observable,
  isUserExit: observable,
  deactivatedAt: observable,
  deactivatedBy: observable,
  deactivatedByUser: observable,
  deactivatedRemark: observable,
  createdAt: observable,
  createdBy: observable,
  updatedAt: observable,
  updatedBy: observable,
  operationNo: observable,
  qualitative: observable,
  quantitative: observable,
  documentIds: observable,
  documents: observable,
  priority: observable,
  isTemplateTask: observable,
  templateHierarchyIds: observable,
  detailsNotAccepted: observable,
});
